import { OrganizationAPI } from '@business/api/organization';
import { TagAPI } from '@business/api/tag';
import { UserId } from '@business/entities/account';
import { PageQuery } from '@business/entities/common';
import {
  ACHMethodRequest,
  ManualSupportHoursRequest,
  OrgAddressResponse,
  OrgBillingType,
  OrgId,
  OrgPageQuery,
  OrgRequest,
  OrgResponse,
  PaymentId,
  PaymentMethodRequest,
  TransactionResponse,
  CollectJsResponse,
  CollectJsFormResponse,
} from '@business/entities/organization';
import { CompactProjectResponse, ConvertProjectRequest } from '@business/entities/project';
import { SubscriptionId } from '@business/entities/project/SubscriptionResponse';
import { InvoiceId } from '@business/entities/TransactionInvoice';
import { handleMessage, requestMessage } from '@business/messages';
import { HTMLService } from '@business/services';
import { BaseBloc } from '@core/utils/bloc';
import { gcLabel } from '@core/utils/hooks/misc/gcLabel';
import { Repository } from '@core/utils/repository';
import { showConfirmation, showDeleteConfirmation } from '@modules/common';
import { SubscriptionRequest } from '@modules/subscription/CreateSubscriptionPage/SubscriptionForm';
import { of } from 'rxjs';
import { switchMap } from 'rxjs/operators';
import { t } from 'ttag';
import { ContactId } from '@business/entities/contact';
import { R } from '@core/utils/r';
export class OrganizationBloc extends BaseBloc {
  private readonly orgRepo = new Repository<OrgResponse>({
    getItemId: org => org.id,
    pagenated: true,
  });

  readonly organizations$ = this.orgRepo.items$;
  readonly selectOrganization = this.orgRepo.selectItem;

  onReset() {
    this.orgRepo.reset();
  }

  fetchOrganizations = (query: PageQuery) => {
    return OrganizationAPI.fetchOrganizations(query);
  };

  //   fetchOrgs = () => {
  //     return OrganizationAPI.fetchOrgs().pipe(
  //       this.orgRepo.ops.upsertMany(item => ({ item })),
  //       handleMessage({ error: requestMessage('fetch_order_products_error') }),
  //     );
  //   };

  fetchOrganization = (id: OrgId) => {
    return OrganizationAPI.fetchOrganization(id).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_orgs_error') }),
    );
  };

  fetchOrganizationDetails = (id: OrgId, onlyInfo = false) => {
    return OrganizationAPI.fetchOrganizationDetails(id, onlyInfo).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_orgs_error') }),
    );
  };

  fetchCompactOrganization = (id: OrgId) => {
    return OrganizationAPI.fetchCompactOrganization(id).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ error: requestMessage('fetch_orgs_error') }),
    );
  };

  fetchClientOrgs = () => {
    return OrganizationAPI.fetchClientOrgs().pipe(this.orgRepo.ops.reset(), handleMessage({ error: requestMessage('fetch_orgs_error') }));
  };

  createOrganization = (org: OrgRequest) => {
    return OrganizationAPI.createOrganization(org).pipe(
      this.orgRepo.ops.addOne(item => item),
      handleMessage({
        type: requestMessage('create_organization'),
      }),
    );
  };

  updateOrganization = (id: OrgId, org: OrgRequest, changeOwnership = false) => {
    return OrganizationAPI.updateOrganization(id, org, changeOwnership).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_organization'),
      }),
    );
  };

  addSubAccount = (id: OrgId, childId: OrgId, billingType: OrgBillingType) => {
    return OrganizationAPI.addSubAccount(id, childId, billingType).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_organization'),
      }),
    );
  };

  deleteSubAccount = (id: OrgId, childId: OrgId) => {
    return showConfirmation('Unlink sub customer', 'Do you really want to unlink this customer?').pipe(
      switchMap(() => OrganizationAPI.deleteSubAccount(id, childId)),
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_organization'),
      }),
    );
  };

  updateWebsites = (id: OrgId, websites: string[]) => {
    return OrganizationAPI.updateWebsites(id, websites).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_organization'),
      }),
    );
  };

  syncOrganization = (id: OrgId) => {
    return OrganizationAPI.syncOrganization(id).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('sync_organization'),
      }),
    );
  };

  syncPayments = (id: OrgId) => {
    return OrganizationAPI.syncPayments(id).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('sync_organization'),
      }),
    );
  };

  updateProject = (id: OrgId, newProject: CompactProjectResponse) => {
    const org = this.orgRepo.getItem(id);
    if (!org) return;
    const projects = org.projects.map(p => {
      if (p.id === newProject.id) {
        return newProject;
      }
      return p;
    });

    this.orgRepo.updateOne({ id, changes: () => ({ projects }) });
  };

  deleteOrganization = (id: OrgId) => {
    return showDeleteConfirmation('Delete Organization', 'Do you really want to remove this organization?').pipe(
      switchMap(() => OrganizationAPI.deleteOrganization(id)),
      this.orgRepo.ops.removeOne(() => id),
      handleMessage({
        type: requestMessage('delete_organization'),
      }),
    );
  };

  searchOrganizations = (search: string) => {
    return OrganizationAPI.fetchOrganizations({ search }).pipe(handleMessage({ error: requestMessage('fetch_orgs_error') }));
  };

  escalateOrganizations = () => {
    return OrganizationAPI.escalateOrgs().pipe(handleMessage({ error: requestMessage('fetch_orgs_error') }));
  };

  createAddress = (orgId: OrgId, data: Partial<OrgAddressResponse>) => {
    return OrganizationAPI.createAddress(orgId, data).pipe(
      switchMap(() => this.fetchOrganizationDetails(orgId)),
      handleMessage({ type: requestMessage('create_address') }),
    );
  };

  updateAddress = (id: string, orgId: OrgId, data: Partial<OrgAddressResponse>) => {
    return OrganizationAPI.updateAddress(id, data).pipe(
      switchMap(() => this.fetchOrganizationDetails(orgId)),
      handleMessage({ type: requestMessage('update_address') }),
    );
  };

  deleteAddress = (id: string, orgId: OrgId) => {
    return showDeleteConfirmation('Delete Address', 'Do you really want to remove this address?').pipe(
      switchMap(() => OrganizationAPI.deleteAddress(id)),
      switchMap(() => this.fetchOrganizationDetails(orgId)),
      handleMessage({
        type: requestMessage('delete_address'),
      }),
    );
  };

  makeAddressPrimary = (id: string, orgId: OrgId) => {
    return OrganizationAPI.makeAddressPrimary(id).pipe(
      switchMap(() => this.fetchOrganizationDetails(orgId)),
      handleMessage({ type: requestMessage('update_address') }),
    );
  };

  createPaymentMethod = (orgId: OrgId, data: PaymentMethodRequest) => {
    return OrganizationAPI.createPaymentMethod(orgId, data).pipe(
      switchMap(() => this.fetchOrganizationDetails(orgId)),
      handleMessage({ type: requestMessage('create_payment') }),
    );
  };

  createAchMethod = (orgId: OrgId, data: ACHMethodRequest) => {
    return OrganizationAPI.createAchMethod(orgId, data).pipe(
      switchMap(() => this.fetchOrganizationDetails(orgId)),
      handleMessage({ type: requestMessage('create_payment') }),
    );
  };

  deletePaymentMethod = (id: PaymentId, orgId: OrgId, subs?: string[]) => {
    const subAlert =
      subs && subs.length > 0
        ? `This Payment method is linked with following subscriptions.
      <br />
      ${HTMLService.toReact(subs.join('<br/>'))} <br /><br />`
        : '';
    return showDeleteConfirmation('Delete Payment Method', subAlert + 'Do you really want to remove this payment method?').pipe(
      switchMap(() => OrganizationAPI.deletePaymentMethod(id)),
      switchMap(() => this.fetchOrganizationDetails(orgId)),
      handleMessage({
        type: requestMessage('delete_payment'),
      }),
    );
  };

  updateTransactions(orgId: OrgId, transactions: TransactionResponse[]) {
    return of(transactions).pipe(
      this.orgRepo.ops.updateOne(() => ({
        id: orgId,
        changes: () => ({ transactions }),
      })),
    );
  }

  createSubscription = (id: OrgId, data: SubscriptionRequest) => {
    return OrganizationAPI.createSubscription(id, data).pipe(
      handleMessage({
        type: requestMessage('create_subscription'),
      }),
    );
  };

  updateSubscription = (id: SubscriptionId, data: SubscriptionRequest) => {
    return OrganizationAPI.updateSubscription(id, data).pipe(
      handleMessage({
        type: requestMessage('update_subscription'),
      }),
    );
  };

  createSubInvoice = (id: SubscriptionId, data: SubscriptionRequest) => {
    return OrganizationAPI.createSubInvoice(id, data).pipe(
      handleMessage({
        type: requestMessage('create_sub_invoice'),
      }),
    );
  };

  resendLastInvoice = (id: SubscriptionId) => {
    return showConfirmation('Resend Invoice', 'Do you really want to send last invoice for this subscription?').pipe(
      switchMap(() => OrganizationAPI.resendLastInvoice(id)),
      handleMessage({
        type: requestMessage('project_invoice'),
      }),
    );
  };

  cancelSubscription = (orgId: OrgId, id: SubscriptionId, reason: string) => {
    return OrganizationAPI.cancelSubscription(id, reason).pipe(
      this.orgRepo.ops.updateOne(subscriptions => ({
        id: orgId,
        changes: () => ({ subscriptions }),
      })),
      handleMessage({
        type: requestMessage('cancel_subscription'),
      }),
    );
  };

  syncSubscription = (orgId: OrgId, id: SubscriptionId) => {
    return OrganizationAPI.syncSubscription(id).pipe(
      this.orgRepo.ops.updateOne(subscriptions => ({
        id: orgId,
        changes: () => ({ subscriptions }),
      })),
      handleMessage({
        type: requestMessage('sync_subscription'),
      }),
    );
  };
  manualSupportHours = (orgId: OrgId, data: ManualSupportHoursRequest) => {
    return OrganizationAPI.manualSupportHours(orgId, data).pipe(
      handleMessage({
        type: requestMessage('manual_support_time'),
      }),
    );
  };

  convertProject = (orgId: OrgId, data: ConvertProjectRequest) => {
    return OrganizationAPI.convertProject(orgId, data).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_project'),
      }),
    );
  };

  changePaymentMethod = (subscriptionId: string, paymentId: PaymentId) => {
    return OrganizationAPI.changePaymentMethod(subscriptionId, paymentId).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('create_project'),
      }),
    );
  };

  changeOwner = (userId: UserId, orgIds: OrgId[], changeOwnership = false) => {
    return OrganizationAPI.changeOwner(userId, orgIds, changeOwnership).pipe(
      handleMessage({
        type: requestMessage('change_owner'),
      }),
    );
  };

  merge = (orgIds: OrgId[], primary: OrgId) => {
    return OrganizationAPI.merge(orgIds, primary).pipe(
      handleMessage({
        type: requestMessage('org_merge'),
      }),
    );
  };

  deleteMultiple = (orgIds: OrgId[]) => {
    return showDeleteConfirmation(
      `Delete ${gcLabel('Organizations')}`,
      `Do you really want to delete ${orgIds.length} ${gcLabel('Organizations')}?`,
    ).pipe(
      switchMap(() => OrganizationAPI.deleteMultiple(orgIds)),
      handleMessage({
        type: requestMessage('delete_multiple_orgs'),
      }),
    );
  };

  sendInvoice = (invoiceId: InvoiceId) => {
    return showConfirmation('Send Invoice', 'Do you really want to send this invoice to the customer?').pipe(
      switchMap(() => OrganizationAPI.sendInvoice(invoiceId)),
      handleMessage({
        type: requestMessage('project_invoice'),
      }),
    );
  };

  voidInvoice = (invoiceId: InvoiceId) => {
    return showDeleteConfirmation('Void Invoice', 'Do you really want to void this invoice to the customer?', { btnLabel: 'Void Invoice' }).pipe(
      switchMap(() => OrganizationAPI.voidInvoice(invoiceId)),
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('invoice_void'),
      }),
    );
  };

  processPayment = (invoiceId: InvoiceId, methodId: PaymentId, amount?: number) => {
    return OrganizationAPI.processPayment(invoiceId, methodId, amount).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({ type: requestMessage('project_process_payment') }),
    );
  };

  attachTags = (orgId: OrgId, tags: string[]) => {
    return TagAPI.attachTags(orgId, 'App\\Models\\Organization', tags).pipe(
      this.orgRepo.ops.updateOne(tags => ({
        id: orgId,
        changes: { tags },
      })),
    );
  };

  detachTag = (orgId: OrgId, tag: string) => {
    return TagAPI.detachTag(orgId, 'App\\Models\\Organization', tag).pipe(
      this.orgRepo.ops.updateOne(tags => ({
        id: orgId,
        changes: { tags },
      })),
    );
  };

  attachTagsContact = (orgId: OrgId, contactId: ContactId, tags: string[]) => {
    return TagAPI.attachTags(contactId, 'App\\Models\\Contact', tags).pipe(
      this.orgRepo.ops.updateOne(tags => ({
        id: orgId,
        changes: org => {
          const contacts = org.contacts;
          const newContact = { ...contacts.find(c => c.id === contactId)! };
          newContact.tags = tags;
          const newContacts = [...contacts.filter(c => c.id !== contactId), newContact];

          return { contacts: newContacts };
        },
      })),
    );
  };

  detachTagContact = (orgId: OrgId, contactId: ContactId, tag: string) => {
    return TagAPI.detachTag(contactId, 'App\\Models\\Contact', tag).pipe(
      this.orgRepo.ops.updateOne(tags => ({
        id: orgId,
        changes: org => {
          const contacts = org.contacts;
          const newContact = { ...contacts.find(c => c.id === contactId)! };
          newContact.tags = tags;
          const newContacts = [...contacts.filter(c => c.id !== contactId), newContact];
          return { contacts: newContacts };
        },
      })),
    );
  };

  export = (query: OrgPageQuery, total: number) => {
    return showConfirmation(t`Export`, t`Do you really want to export ${total} ${gcLabel('Organizations')}?`, { btnYesLabel: t`Export` }).pipe(
      switchMap(() => OrganizationAPI.exportOrganizations(query)),
      handleMessage({ type: requestMessage('org_export') }),
    );
  };

  mergeContacts = (orgId: OrgId, contacts: ContactId[], primary: ContactId) => {
    return OrganizationAPI.mergeContacts(orgId, contacts, primary).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('contact_merge'),
      }),
    );
  };

  makeDefault = (id: string) => {
    return OrganizationAPI.makeDefaultPaymentMethod(id).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_payment'),
      }),
    );
  };

  updateSubPaymentMethod = (id: string) => {
    return OrganizationAPI.updateSubPaymentMethod(id).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_payment'),
      }),
    );
  };

  showOnBoard = (id: string) => {
    return OrganizationAPI.showOnBoard(id).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_project'),
      }),
    );
  };

  downloadInvoice = (id: string, filename: string) => {
    return OrganizationAPI.downloadInvoice(id, filename).pipe(handleMessage({ error: requestMessage('download_invoice_error') }));
  };

  syncInvoice = (id: string) => {
    return OrganizationAPI.syncInvoice(id).pipe(handleMessage({ type: requestMessage('sync_invoice') }));
  };

  fetchOrganizationDefaultAddress = (id: OrgId) => {
    return OrganizationAPI.fetchOrganizationDefaultAddress(id).pipe(handleMessage({ error: requestMessage('fetch_org_address_error') }));
  };

  updateSaleReps = (id: OrgId, saleReps: UserId[]) => {
    return OrganizationAPI.updateSaleReps(id, saleReps).pipe(
      this.orgRepo.ops.upsertOne(item => ({ item })),
      handleMessage({
        type: requestMessage('update_organization'),
      }),
    );
  };
}
